import { createI18n } from 'vue-i18n';
import { log } from '@/utils/core/vueLogger.js';

export const SUPPORTED_LOCALES = ['fr-FR', 'en-GB', 'es-ES', 'pt-PT', 'nl-NL', 'de-DE'];
export const DEFAULT_LOCALE = 'fr-FR';

// Lazy locale message loaders — each locale is a separate chunk, so only the
// active language is ever fetched (the "one language at a time" requirement).
const localeLoaders = import.meta.glob('./locales/*.json');

// Legacy keys are literal strings (e.g. 'My profile', 'Home.Welcome', 'DOCUMENT_SYNC.').
// A flat resolver keeps dots/spaces literal instead of treating them as nested paths,
// matching legacy $T() lookup semantics exactly.
//
// applicationResources-sourced entries are merged in lower-cased (see translationOverlay.js)
// because legacy $T() always matched them case-insensitively, and call sites use inconsistent
// casing for the same key. Try the exact key first (matches the base locale JSON, authored
// with intentional casing), then fall back to a lower-cased lookup for overlay entries.
function flatMessageResolver(obj, path) {
	if (obj == null) return null;
	if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];
	const lower = path.toLowerCase();
	if (lower !== path && Object.prototype.hasOwnProperty.call(obj, lower)) return obj[lower];
	return null;
}

export const i18n = createI18n({
	legacy: false,
	globalInjection: true,
	locale: DEFAULT_LOCALE,
	fallbackLocale: DEFAULT_LOCALE,
	messages: {},
	messageResolver: flatMessageResolver,
	missingWarn: false,
	fallbackWarn: false,
	warnHtmlMessage: false,
	// Values can be plain strings with literal '{...}' that are not interpolations.
	escapeParameter: false,
});

const _loaded = new Set();

/**
 * Normalize an arbitrary locale (e.g. 'en', 'en-US') to a supported full locale code.
 */
export function normalizeLocale(raw) {
	if (!raw) return DEFAULT_LOCALE;
	if (SUPPORTED_LOCALES.includes(raw)) return raw;
	const short = raw.split('-')[0].toLowerCase();
	return SUPPORTED_LOCALES.find((l) => l.split('-')[0].toLowerCase() === short) || DEFAULT_LOCALE;
}

/**
 * Lazy-load the base message file for a locale (once). Only this locale's chunk is fetched.
 */
export async function loadLocaleMessages(locale) {
	const normalized = normalizeLocale(locale);
	if (_loaded.has(normalized)) return normalized;

	const loader = localeLoaders[`./locales/${normalized}.json`];
	if (!loader) {
		log('warn', 'i18n', `No locale file for ${normalized}`);
		_loaded.add(normalized);
		return normalized;
	}

	try {
		const mod = await loader();
		// Merge so a later applicationResources overlay can override the base.
		i18n.global.mergeLocaleMessage(normalized, mod.default || mod);
		_loaded.add(normalized);
		log('success', 'i18n', `Loaded base messages for ${normalized}`);
	} catch (e) {
		log('error', 'i18n', `Failed to load messages for ${normalized}: ${e.message}`);
	}
	return normalized;
}

/**
 * Load (if needed) and activate a locale. Returns the normalized locale code.
 */
export async function setLocale(locale) {
	const normalized = await loadLocaleMessages(locale);
	i18n.global.locale.value = normalized;
	return normalized;
}

export function getActiveLocale() {
	return i18n.global.locale.value;
}

/**
 * Translation helper for plain JS modules (no component instance).
 * Components should prefer useI18n()/useTranslator().
 */
export function t(key, ...args) {
	return i18n.global.t(key, ...args);
}
